import { useEffect, useState, useRef, useCallback } from 'react'
import { useTranslation } from "react-i18next"
import styled from 'styled-components'
import dayjs from 'dayjs'
import { Checkbox, Form, Select } from 'antd'
import duration from 'dayjs/plugin/duration'
import { LabelBox } from '../components/base-ui'
import { OrderSummaryDataType, FormTaxInvoiceType, PrivateRoomType } from '../types'
import {  WpEnvCityEnum } from '../enums'

import { displayPrice2Digit } from '../helpers'

interface SelectCourseCheckoutProps {
  isAdmin: boolean
  isAdminMode: boolean
  orderSummaryData: OrderSummaryDataType
  taxInvoiceFormData?: FormTaxInvoiceType
  isRequestingUpdateTaxInvoiceOrder: boolean
  onCheckout: (values: FormTaxInvoiceType) => void
  wpEnvCity: keyof typeof WpEnvCityEnum | ''
  listPrivateRoom: PrivateRoomType[]
}

dayjs.extend(duration)

const SelectCourseCheckout = ({ taxInvoiceFormData, isAdmin, isAdminMode, orderSummaryData, onCheckout, isRequestingUpdateTaxInvoiceOrder, wpEnvCity, listPrivateRoom }: SelectCourseCheckoutProps) => {
  const { t } = useTranslation()
  const [formValidateTriggerTaxID, setFormValidateTriggerTaxID] = useState<string>('onBlur')
  const [formValidateTriggerBranchNumber, setFormValidateTriggerBranchNumber] = useState<string>('onBlur')
  const [form] = Form.useForm<FormTaxInvoiceType>()
  const is_checked_tax_invoice = Form.useWatch('tax_invoice', form)

  const onFinish = (values: FormTaxInvoiceType) => {
    onCheckout(values)
  }

  const onFinishFailed = (errorInfo: any) => {
    setFormValidateTriggerTaxID('onChange')
    setFormValidateTriggerBranchNumber('onChange')
    console.log('Failed:', errorInfo)
  }

  const getDefaultSelectCountry = (wpEnvCity: SelectCourseCheckoutProps['wpEnvCity']) => {
    switch(wpEnvCity){
      case 'th':
        return wpEnvCity
      default:
        return 'other'
    }
  }

  return (
    <Wrapper className="yz-summary" id="step-checkout-summary">
      <Form
        form={form}
        className="yz-form"
        autoComplete="off"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{ ...taxInvoiceFormData, select_country: getDefaultSelectCountry(wpEnvCity) }}
      >
        <div className="yz-row">
          <div>{t('confirm_your_reservation')} :</div>
        </div>

        <div className="yz-divider"></div>

        <div className="yz-row">
          <div>{t('order_number')}</div><div>#{orderSummaryData.order_number}</div>
        </div>

        {
          orderSummaryData.tickets.map((ticket, i) => {
            if (ticket.persons > 0) {
              const privateRoom = listPrivateRoom.find(room => room.name.toLowerCase() === ticket.name.toLowerCase())

              return privateRoom? (
                <div key={i}>
                  <div className="yz-row">
                    <div>{t('room')}: {ticket.name}</div><div>{displayPrice2Digit(ticket.price)}</div>
                  </div>
                  <div className="yz-row">
                    <div>{t('number_of_party')}</div><div>x{orderSummaryData.number_of_party} {t('persons')}</div>
                  </div>
                </div>
              ): (
                <div key={i}>
                  <div className="yz-row">
                    <div>{t('course')}: {ticket.name}</div><div>{displayPrice2Digit(ticket.price)}</div>
                  </div>
                  <div className="yz-row">
                    <div>{t('number_of_party')}</div><div>x{ticket.persons} {t('persons')}</div>
                  </div>
                </div>
              )
            } else {
              return null
            }
          })
        }

        <div className="yz-row">
          <div>{t('date_and_time')}</div><div>{dayjs(orderSummaryData.start_date_time).format('DD/MM/YYYY HH:mm')}</div>
        </div>
        <div className="yz-row">
          <div>{t('contact_detail')}</div><div>{`${orderSummaryData.first_name} ${orderSummaryData.last_name}`.trim()}</div>
        </div>
        <div className="yz-row">
          <div></div><div>{orderSummaryData.email}</div>
        </div>
        <div className="yz-row">
          <div></div><div>+{orderSummaryData.phone}</div>
        </div>

        <div className="yz-divider"></div>

        <div className="yz-row">
          <div>{t('subtotal')}</div><div>{displayPrice2Digit(orderSummaryData.total_amount)}</div>
        </div>
        <div className="yz-row">
          <div>{t('deposit_amount')} ({orderSummaryData.deposit_amount/orderSummaryData.total_amount*100}%)</div><div>{displayPrice2Digit(orderSummaryData.deposit_amount)}</div>
        </div>
        {/* <div className="yz-row">
          <div>VAT (7%) / Included</div><div>{displayPrice2Digit(orderSummaryData.vat_amount)}</div>
        </div> */}

        <div className="yz-divider"></div>
        <div className="yz-row">
          <div>{t('total')}</div><div><strong>{displayPrice2Digit(orderSummaryData.deposit_amount)}</strong></div>
        </div>
        <div className="yz-divider"></div>
        
        <div className="yz-input-wrapper -checkbox">
          <Form.Item
            name="tax_invoice"
            valuePropName="checked"
          >
            <Checkbox>{t('tax_invoice')}</Checkbox>
          </Form.Item>
        </div>
        {
          is_checked_tax_invoice && (
            <TaxInvoiceWrapper>
              <div className="yz-input-wrapper">
                <Form.Item
                  name="select_country"
                >
                  <Select>
                    <Select.Option value="th"><OptionCountry><div className="react-tel-input"><div className="flag th"></div></div>{t('thai')}</OptionCountry></Select.Option>
                    <Select.Option value="other"><OptionCountry>{t('other')}</OptionCountry></Select.Option>
                  </Select>
                </Form.Item>
              </div>
              <div className="yz-text-sm yz-input-label">{t('company_name')}*</div>
              <div className="yz-input-wrapper">
                <Form.Item
                  name="company_name"
                  rules={[{ required: true, message: `${t('company_name_validate_empty')}!` }]}
                >
                  <input type="text" placeholder={t('company_name_placeholder')} />
                </Form.Item>
              </div>
              <div className="yz-text-sm yz-input-label">{t('address')} 1*</div>
              <div className="yz-input-wrapper">
                <Form.Item
                  name="address_1"
                  rules={[{ required: true, message: `${t('address_validate_empty')}!` }]}
                >
                  <textarea rows={3} placeholder={t('address_placeholder')}></textarea>
                </Form.Item>
              </div>
              <div className="yz-text-sm yz-input-label">{t('address')} 2</div>
              <div className="yz-input-wrapper">
                <Form.Item
                  name="address_2"
                >
                  <textarea rows={3} placeholder={t('address_placeholder')}></textarea>
                </Form.Item>
              </div>
              <div className="input-group">
                <div>
                  <div className="yz-text-sm yz-input-label">{t('tax_id')}*</div>
                  <div className="yz-input-wrapper">
                    <Form.Item
                      noStyle
                      dependencies={['select_country']}
                    >
                      {({ getFieldValue }) => {
                        return getFieldValue('select_country') === 'th' ? (
                          <Form.Item
                            name="tax_id"
                            dependencies={['select_country']}
                            validateTrigger={formValidateTriggerTaxID}
                            rules={[
                              { pattern: new RegExp(/^\d{13}$/g), required: true, message: t('tax_id_validate_13_numbers')},
                            ]}
                          >
                            <input inputMode="numeric" placeholder={`${t('tax_id_placeholder')}!`} onBlur={() => setFormValidateTriggerTaxID('onChange')} maxLength={13}/>
                          </Form.Item>
                        ) : (
                          <Form.Item
                            name="tax_id"
                            dependencies={['select_country']}
                            rules={[{ required: true, message: `${t('tax_id_validate_empty')}!` }]}
                          >
                            <input type="text" placeholder={t('tax_id_placeholder')}/>
                          </Form.Item>
                        )
                      }}
                    </Form.Item>
                  </div>
                </div>
                <div>
                  <div className="yz-text-sm yz-input-label">{t('branch_number')}</div>
                  <div className="yz-input-wrapper">
                    <Form.Item
                      noStyle
                      dependencies={['select_country']}
                    >
                      {({ getFieldValue }) => {
                        return getFieldValue('select_country') === 'th' ? (
                          <Form.Item
                            name="branch_number"
                            dependencies={['select_country']}
                            validateTrigger={formValidateTriggerBranchNumber}
                            rules={[
                              { pattern: new RegExp(/^\d{5}$/g), message: t('branch_number_validate_5_numbers')},
                            ]}
                          >
                            <input inputMode="numeric" placeholder={t('branch_number_placeholder')} onBlur={() => setFormValidateTriggerBranchNumber('onChange')} maxLength={5}/>
                          </Form.Item>
                        ) : (
                          <Form.Item
                            name="branch_number"
                            dependencies={['select_country']}
                          >
                            <input type="text" placeholder={t('branch_number_placeholder')}/>
                          </Form.Item>
                        )
                      }}
                    </Form.Item>
                  </div>
                </div>
              </div>
              <div className="yz-text-sm yz-input-label">{t('branch_name')}</div>
              <div className="yz-input-wrapper">
                <Form.Item
                  name="branch_name"
                >
                  <input type="text" placeholder={t('branch_name_placeholder')} />
                </Form.Item>
              </div>
            </TaxInvoiceWrapper>
          )
        }

        <LabelBoxWrapper>
          <LabelBox type="warning">
            {t('notice_full_tax_warning')}
          </LabelBox>
        </LabelBoxWrapper>

        <SmalllNotion>{t('notice_first_tax_invoice_deposit_amount_within_12_hours')}</SmalllNotion>

        <Timer>
          <LabelBoxWrapper>
            <LabelBox type="warning">
              {
                wpEnvCity === 'vn' ?
                  t('notice_accept_one_time_changes_booking')
                  :
                  t('notice_changes_reservation_are_not_permitted')
              }
            </LabelBox>
          </LabelBoxWrapper>
          <div className="yz-row">
            <div>{t('click_to_select_payment')}</div>
          </div>
          <div className="yz-row">
            <Countdown dateCreated={orderSummaryData.date_created}/>
            {
              isAdmin && (
                <PaymentLink onClick={() => copyText('order-payment-link')}>
                  <i className="icon-yuzu-link" /><span>{t('copy_link')}</span>
                  <input id="order-payment-link" type="text" value={`${window.location.origin}${window.location.pathname}?order_number=${orderSummaryData.order_number}`} />
                </PaymentLink>
              )
            }
          </div>
        </Timer>

        <Footer>
          <div className="yuzu-btn -outline -lg" onClick={() => {
            const urlAdminMode = isAdminMode ? '?mode=admin' : ''
            window.location.href = `${window.location.origin}${window.location.pathname}${urlAdminMode}`
          }}><span>{t('back')}</span></div>
          <div 
            className="yuzu-btn -lg" 
            style={{ minWidth: '176px' }} 
            onClick={() => form.submit()}
          >
            {
              isRequestingUpdateTaxInvoiceOrder ? (
                <svg
                  width="30px"
                  height="30px"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMidYMid"
                  className="lds-rolling"
                >
                  <circle cx="50" cy="50" fill="none" ng-attr-stroke="{{config.color}}" ng-attr-stroke-width="{{config.width}}" ng-attr-r="{{config.radius}}" ng-attr-stroke-dasharray="{{config.dasharray}}" stroke="#fff" strokeWidth="10" r="35" strokeDasharray="164.93361431346415 56.97787143782138" transform="rotate(162 50 50)"><animateTransform attributeName="transform" type="rotate" calcMode="linear" values="0 50 50;360 50 50" keyTimes="0;1" dur="400ms" begin="0s" repeatCount="indefinite"></animateTransform></circle>
                </svg>
              ) : (
                <span>{t('checkout')}</span>
              )
            }
          </div>
        </Footer>
      </Form>
    </Wrapper>
  )

  function copyText(inputId: string) {
    const el = document.getElementById(inputId) as HTMLInputElement

    if (navigator.userAgent.match(/(iPhone|iPod|iPad)/i)) {
      const oldContentEditable = el.contentEditable,
        oldReadOnly = el.readOnly,
        range = document.createRange()

      el.contentEditable = 'true'
      el.readOnly = false
      range.selectNodeContents(el)

      const s = window.getSelection()
      if (s) {
        s.removeAllRanges()
        s.addRange(range)
      }

      el.setSelectionRange(0, 999999)

      el.contentEditable = oldContentEditable
      el.readOnly = oldReadOnly
    } else {
      el.select()
    }

    document.execCommand('copy') 
    alert('Copied Payment Link!')
  }
}

const expired = 15 // min
const Countdown = ({ dateCreated }: { dateCreated:string }) => {
  const { t } = useTranslation()
  const refTimer = useRef<ReturnType<typeof setInterval>>()

  const getRemainingTime = useCallback(() => {
    const msDuration = dayjs(dateCreated).add(expired, 'minute').diff(dayjs(), 'ms')
    if (msDuration < 0) {
      clearInterval(refTimer.current)
      window.location.href = `${window.yuzuBaseURL}/session-expired`
      return '00:00'
    }
    return dayjs.duration(msDuration).format('mm:ss')
  }, [dateCreated])

  const [time, setTime] = useState<string>(getRemainingTime())

  useEffect(() => {
    refTimer.current = setInterval(() => setTime(getRemainingTime()), 1000)
    return () => clearInterval(refTimer.current)
  }, [dateCreated, getRemainingTime])
  return (<div>{t('timer')} : {time}</div>)
}

export default SelectCourseCheckout

const Wrapper = styled.div`
  max-width: 500px;

  .yz-row {
    color: white;
  }
`

const Timer = styled.div`
  margin-top: 24px;
`

const Footer = styled.div`
  margin-top: 64px;
  margin-bottom: 80px;
  display: flex;
  justify-content: space-between;
`

const LabelBoxWrapper = styled.div`
  margin-bottom: 16px;
`

const PaymentLink = styled.div`
  position: relative;
  cursor: pointer;
  color: var(--global--color-background-secondary);

  i {
    margin-right: 8px;
    font-size: 14px;
  }

  span {
    text-decoration: underline;
    font-size: 14px;
  }

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`

const TaxInvoiceWrapper = styled.div`
  margin-top: 16px;
`

const SmalllNotion = styled.div`
  margin-top: 10px;
  font-size: 10px;
  color: rgba(255,255,255, .6);
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 767px) {
    max-width: initial;
  }
`

const OptionCountry = styled.div`
  display: inline-flex;
  align-items: center;
  column-gap: 5px;
  color: black;
  height: 100%;
`